import React, { useEffect } from "react";
import { IBaseProperty, ISerializedUIField, IWDDesignModeProps, PropertyPath } from "../../common";
import { IContextProvider } from "../../../../uxp";
import { Button, HorizontalScrollList } from "uxp/components";
import { IDataSourceBinding } from "../../data-source-picker/datasource";
import './CardList.scss';
import { hasValue, replaceWithData } from "../../../../util";
import { FieldContainer } from "../../FieldContainer";
import { IMarginProperty } from "../../ui-props/MarginProperty";
import { IAction } from "../../action-editor/ActionEditor";
import ActionRenderComponent from "../ActionRenderComponent";
import classNames from "classnames";
import { StaticFieldsList } from "../../StaticFieldsList";

export type ICardType = 'card-1' | 'card-2' | 'card-3'
export interface ICardListProps {
    imageField?: string,
    imagePosition?: string,
    imageSize?: string,
    titleField?: string,
    titleColor?: string,
    titleSize?: string,
    subTitleField?: string,
    subTitleColor?: string,
    subTitleSize?: string,
    binding?: IDataSourceBinding
    fields?: ISerializedUIField[]
    aspectRatio?: string,
    height?: string,
    marginTop?: string,
    marginBottom?: string,
    onClick?: IAction
    bgColor?: string
    type?: ICardType,
    buttonLabel?: string,
    buttonBG?: string,
    buttonLabelColor?: string,
}
export interface ICardList {
    uxpContext: IContextProvider,
    designer: IWDDesignModeProps,
    items: any[],
    cardListProps: ICardListProps,

    contextData?: any,
    onChangeContextData?: (contextData: any) => void
    propPath?: PropertyPath,
    dataKey?: string,
    droppableParentContainerId?: string,
    disableDrop?: boolean,
    onChangeFields?: (newFields: ISerializedUIField[]) => void
    uiProps?: any,
    instanceId?: string
}

export const CardList: React.FunctionComponent<ICardList> = (props) => {

    const { uxpContext, designer, items, cardListProps, contextData, uiProps, instanceId, propPath } = props
    const isDesignMode = designer && (designer.mode == 'preview' || designer.mode == 'thumbnail')

    let { imageField, titleField, subTitleField } = cardListProps || { imageField: '', titleField: '', subTitleField: '' }
    if (isDesignMode) {
        if (!hasValue(imageField)) imageField = '{$.row.image}'
        if (!hasValue(titleField)) titleField = '{$.row.title}'
        if (!hasValue(subTitleField)) subTitleField = '{$.row.subTitle}'
    }

    const [actionData, setActionData] = React.useState<{ action: IAction, contextData: any }>({ action: null, contextData: {} })

    function handleClick(item: any) {
        if (isDesignMode) return
        if (!cardListProps.onClick) return
        setActionData({ action: cardListProps.onClick, contextData: { ...contextData, row: item } })
    }
    const type = cardListProps?.type || 'card-1'
    const bottomContainerStyles: React.CSSProperties = {}
    if (type == 'card-2' && hasValue(cardListProps?.bgColor)) bottomContainerStyles.backgroundColor = cardListProps.bgColor

    const buttonStyles: React.CSSProperties = {}
    if (hasValue(cardListProps?.buttonBG)) buttonStyles.backgroundColor = cardListProps.buttonBG
    if (hasValue(cardListProps?.buttonLabelColor)) buttonStyles.color = cardListProps.buttonLabelColor

    return <div className="uxp-card-list-widget-card-container">
        <HorizontalScrollList
            items={items}
            renderItem={(item: any, key: number) => {
                const image = replaceWithData(imageField || '', { row: { ...item } })
                const title = replaceWithData(titleField || '', { row: { ...item } })
                const subTitle = replaceWithData(subTitleField || '', { row: { ...item } })

                const imageStyles: React.CSSProperties = {}
                if (hasValue(image)) imageStyles.backgroundImage = `url(${image})`
                if (hasValue(cardListProps?.imagePosition)) imageStyles.backgroundPosition = cardListProps.imagePosition
                if (hasValue(cardListProps?.imageSize)) imageStyles.backgroundSize = cardListProps.imageSize

                const titleStyles: React.CSSProperties = {}
                if (hasValue(cardListProps?.titleColor)) titleStyles.color = cardListProps.titleColor
                if (hasValue(cardListProps?.titleSize)) titleStyles.fontSize = cardListProps.titleSize

                const subTitleStyles: React.CSSProperties = {}
                if (hasValue(cardListProps?.subTitleColor)) subTitleStyles.color = cardListProps.subTitleColor
                if (hasValue(cardListProps?.subTitleSize)) subTitleStyles.fontSize = cardListProps.subTitleSize

                return <div className="uxp-card-list-widget-card" >
                    <div className="image-container" style={imageStyles} >

                        {['card-1', 'card-2'].includes(type) &&
                            <div className="first-container">
                                <StaticFieldsList
                                    types={['label', 'button']}
                                    designer={designer}
                                    uxpContext={uxpContext}
                                    uiProps={uiProps}
                                    fields={uiProps?.fields || []}
                                    containerId={`${instanceId}-card-top-fields`}
                                    onChangeLabels={(newLabels: ISerializedUIField[]) => {
                                        props.onChangeFields(newLabels)
                                    }}
                                    contextData={{ ...contextData, row: item }}
                                    propPath={propPath}
                                />
                            </div>
                        }

                        <div className={classNames("second-container", type)} style={bottomContainerStyles} >
                            <div className="titles">
                                <div className="title" style={titleStyles}>{title}</div>
                                <div className="sub-title" style={subTitleStyles}
                                    onClick={e => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        handleClick(item)
                                    }}
                                >{subTitle}</div>
                            </div>

                            <div className="fields">
                                {type != 'card-3' &&
                                    <StaticFieldsList
                                        types={['label', 'button']}
                                        designer={designer}
                                        uxpContext={uxpContext}
                                        uiProps={uiProps}
                                        fields={uiProps?.fields || []}
                                        containerId={`${instanceId}-card-bottom-fields`}
                                        onChangeLabels={(newLabels: ISerializedUIField[]) => {
                                            props.onChangeFields(newLabels)
                                        }}
                                        contextData={{ ...contextData, row: item }}
                                        propPath={propPath}
                                    />
                                }
                            </div>
                        </div>

                        {type == 'card-3' &&
                            <div className="third-container">
                                <Button
                                    title={cardListProps?.buttonLabel || 'Click me'}
                                    onClick={() => handleClick(item)}
                                    styles={buttonStyles}
                                />
                            </div>
                        }
                    </div>
                </div>
            }}
        />

        <ActionRenderComponent
            designer={designer}
            uxpContext={uxpContext}
            {...actionData}
            onClose={() => setActionData({ action: null, contextData: {} })}
            onReloadWidget={() => { }}
        />

    </div>
}